import type { MenuItemState } from "@fluentui/react-components";
import {
  makeStyles,
  mergeClasses,
  useMenuItemStyles_unstable,
  shorthands,
} from "@fluentui/react-components";
import { customTokens } from "@1js/acui-theme";
// eslint-disable-next-line no-restricted-imports
import {
  HighContrastSelector,
  commonHighContrastHoverFocus,
  commonHighContrastColorDisabled,
  commonHighContrastHoveredDisabledStyles,
} from "@1js/acui-common/lib/styling/next";

export const useAppMenuItemCustomStyles = makeStyles({
  root: {
    ...shorthands.borderRadius("4px"),
    ...shorthands.gap("8px", "0px"),
    ...shorthands.margin("0px", "6px"),
    ...shorthands.padding("0px", "6px"),
    ...shorthands.borderWidth("0px"),
    height: "32px",
    flexShrink: 0,
    alignItems: "center",
    minWidth: "224px",
    ":hover": {
      [`${HighContrastSelector}`]: {
        ...commonHighContrastHoverFocus,
      },
    },
    ":hover:active": {
      [`${HighContrastSelector}`]: {
        ...commonHighContrastHoverFocus,
      },
    },
  },
  icon: {
    ...shorthands.padding("6px", "0px"),
    alignItems: "flex-start",
  },
  content: {
    alignItems: "center",
    fontFamily: customTokens.fontFamilyBase,
    ...shorthands.flex(1, 0),
    ...shorthands.padding("6px", "0px"),
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    ...shorthands.overflow("hidden"),
  },
  secondaryContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    fontFamily: customTokens.fontFamilyBase,
    ...shorthands.gap("2px"),
    ...shorthands.padding("6px", "0px", "6px", "4px"),
  },
  submenuIndicator: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    ...shorthands.padding("6px", "0px", "6px", "4px"),
  },
  divider: {
    display: "flex",
    ...shorthands.margin("3px", "0px"),
    ...shorthands.borderBottom(
      "1px",
      "solid",
      customTokens.colorNeutralStroke2
    ),
    flexDirection: "column",
    alignItems: "flex-start",
  },
  menuGroupHeader: {
    flexDirection: "column",
    alignItems: "flex-start",
    ...shorthands.borderRadius("8px"),
    ...shorthands.padding("8px", "12px"),
    height: "16px",
    alignSelf: "stretch",
    color: customTokens.colorNeutralForeground1,
    lineHeight: "16px",
  },
  splitButton: {
    display: "flex",
    alignItems: "center",
    minWidth: "unset",
    justifyContent: "flex-end",
    ...shorthands.padding("6px", "6px", "6px", "0px"),
    ...shorthands.margin("0px", "6px", "0px", "-6px"),
    ...shorthands.gap("0px"),
  },
  subMenu: {
    minWidth: "unset",
  },
  disabled: {
    ":hover:active": {
      // For some unknown reasons, the pressed state for a disabled menu item shows a bg color on it.
      // Hence, explicitly adding the correct styling to override the pressed state.
      backgroundColor: customTokens.colorNeutralBackground1,
      color: customTokens.colorNeutralForegroundDisabled,
      [`${HighContrastSelector}`]: {
        ...commonHighContrastColorDisabled,
        ...commonHighContrastHoveredDisabledStyles,
      },
    },
    ":hover": {
      [`${HighContrastSelector}`]: {
        ...commonHighContrastColorDisabled,
        ...commonHighContrastHoveredDisabledStyles,
      },
    },
  },
  checked: {
    borderRadius: customTokens.borderRadiusMedium,
    ...shorthands.padding("0px", "5px"), // dropping the padding 1px to cater to the border
    border: `1px solid ${customTokens.colorNeutralForeground4}`,
    backgroundColor: `${customTokens.colorNeutralBackground1Selected} !important`, // adding !important to override the hover bg color
  },
  shortcutPillsTagGroup: {
    gap: "2px",
  },
});

export const useAppMenuItemStyles = (state: MenuItemState) => {
  const styles = useAppMenuItemCustomStyles();
  state.root.className = mergeClasses(
    "ms-MenuButtonNext",
    styles.root,
    state.disabled && styles.disabled,
    state.root.className
  );

  if (state.icon) {
    state.icon.className = mergeClasses(styles.icon, state.icon.className);
  }
  if (state.content) {
    state.content.className = mergeClasses(
      styles.content,
      state.content.className
    );
  }

  if (state.secondaryContent) {
    state.secondaryContent.className = mergeClasses(
      styles.secondaryContent,
      state.secondaryContent.className
    );
  }
  if (state.submenuIndicator) {
    state.submenuIndicator.className = mergeClasses(
      styles.secondaryContent,
      state.submenuIndicator.className
    );
  }

  useMenuItemStyles_unstable(state);
  return state;
};
